import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { type CameraFeature } from '@tsr/shared-types';
import { CameraMedia } from './CameraMedia';
import { NewsMedia } from './NewsMedia';
import { DroneMedia } from './DroneMedia';
import { usePinnedFeeds } from './pinnedFeeds';
import {
  CANADA_BBOX,
  fetchCameras,
  fetchHighRiskHotspots,
  fetchSmokeDetectionAnomalies,
  fetchIceStormAnomalies,
  fetchFloodAnomalies,
} from './api';
import {
  hotspotToQueueItem,
  smokeToQueueItem,
  iceStormToQueueItem,
  floodToQueueItem,
  type QueueItem,
} from './anomalyQueue';
import {
  bankWindow,
  selectWallFeeds,
  type WallAlertAnchor,
  type WallCameraCandidate,
  type WallEscalatedTarget,
  type WallFeed,
} from './wallMedia';
import { newsFallbackFor } from './wallNewsFallback';
import { isEscalationStale, useWallEscalation, type WallTarget } from './selectionBridge';
import { reportWallFeedPlayback, wallDeadFeedIds } from './wallFeedHealth';
import {
  classifyWallMedia,
  wallMediaLabelForCamera,
  type WallMediaLabel as WallMediaLabelInfo,
  type WallMediaProps,
  type WallPlaybackState,
} from './wallMediaLabel';
import {
  WALL_REEL_DWELL_MS,
  WALL_REEL_MAX_PAGES,
  reelWindow,
  shouldAdvanceReel,
} from './wallReel';

/**
 * Wall media zone (#188; v2 stacked flanks + curation, #269) — a media bank of
 * `cols × rows` full-bleed tiles (PART X: GOC flank = 1 × flankRows stack,
 * PEOC flank = K panels × 2 rows, seam-aligned).
 *
 * DEFAULT = ALERT-DRIVEN with Ruling 4 curation: seating prefers CURRENTLY-
 * BROADCASTING feeds (the #256 classification axes + the #232 runtime-honesty
 * signal from `wallFeedHealth`), then alert-proximity among the live; pinned
 * feeds ride as operator intent; snapshots/clips seat only once broadcasting
 * candidates are exhausted; honest-OFFLINE only when nothing live qualifies.
 * Re-derives on a wall cadence so the mosaic tracks the picture without an
 * operator — and a tile that DIES mid-dwell is demoted at the next pass, so
 * the rotation replaces it instead of re-seating it.
 *
 * BORDERLESS / full-bleed: on the wall the tiles meet edge-to-edge (no card
 * chrome). A subtle overlay caption (camera/location + the honest state dot)
 * keeps each feed identifiable at wall distance.
 */

/** Candidate camera pool = smoke-priority cameras (the operationally-relevant set
 *  that could show a hazard) — a bounded ~hundreds-row fetch, not the 10k fleet. */
const MEDIA_REFRESH_MS = 30_000;

/** Stills-exception freshness window (#273, PART XI Ruling 5.5): a featured
 *  snapshot is FRESH while captured ≤30 min ago. Since #382 freshness is a
 *  seating PREFERENCE (fresh featured stills are preferred for the curated
 *  seat), not a GATE — a stale featured still still seats (the operator's
 *  explicit Feature is honoured), it just ranks below the fresh ones. */
const FEATURED_SNAPSHOT_FRESH_MS = 30 * 60_000;

/** Tile blow-up auto-restore window (#273, PART XI Ruling 4.3) — a blown tile
 *  returns the bank to its default layout after ~5 min unless the operator
 *  restores it sooner (click again). `?blowup=N` (ms) overrides for QA/demo. */
const WALL_BLOWUP_RESTORE_MS = 5 * 60_000;

/** While a tile is blown, poll this often for the "blown tile died → restore
 *  immediately" rule (Ruling 4.3) — a bounded, rare state, so cheap. */
const BLOWUP_DEATH_POLL_MS = 2_000;

function hazardLabel(source: QueueItem['source']): string {
  switch (source) {
    case 'hotspot':
      return 'High-Risk Hotspot';
    case 'smoke':
      return 'Camera Smoke';
    case 'ice_storm':
      return 'Ice Storm';
    case 'flood':
      return 'Flood';
    default:
      return 'Alert';
  }
}

function toAnchor(it: QueueItem): WallAlertAnchor {
  return { id: it.id, label: hazardLabel(it.source), rank: it.rank, timeIso: it.timeIso, lat: it.lat, lon: it.lon };
}

/** Build the `escalated` selection input for a live operator escalation (#222).
 *  null when there's no active/fresh escalation. A `camera` target forces its
 *  exact feed; an `incident` target frames the nearest live cameras to its point. */
function toEscalatedTarget(target: WallTarget | null, nowMs: number): WallEscalatedTarget | null {
  if (!target || isEscalationStale(target, nowMs)) return null;
  return {
    cameraId: target.kind === 'camera' ? target.id : undefined,
    lat: target.lat,
    lon: target.lon,
    label: target.label,
  };
}

export function WallMediaZone({
  cols,
  rows,
  feedOffset = 0,
  dwellMs = WALL_REEL_DWELL_MS,
  tourCard = null,
  blowupMode = 'quad',
  blowupRestoreMs = WALL_BLOWUP_RESTORE_MS,
  allowsNews = false,
}: {
  /** Tile grid shape (PART X): `cols × rows` full-bleed tiles. */
  cols: number;
  rows: number;
  /** This bank's slot offset in the shared alert-driven ranking — lets the
   *  left + right flanks show DISTINCT cameras. Scaled internally by the reel
   *  depth (`poolOffset`) so sibling banks' candidate POOLS are disjoint
   *  across every reel page, not just the first. */
  feedOffset?: number;
  /** Reel dwell (ms) — how long each page of candidates stays on screen (#222). */
  dwellMs?: number;
  /** PEOC tour SITREP card (#272, Ruling 2.5): during a tour-stop dwell the
   *  card SEATS as this bank's first tile, displacing one feed (floating
   *  cards over a strip hero are forbidden). Null = no card (quiet stop /
   *  no active stop / GOC, which overlays the hero corner instead). */
  tourCard?: JSX.Element | null;
  /** Tile blow-up geometry (#273, PART XI Ruling 4.2): PEOC (panel grammar) =
   *  'quad' (click → a 2×2 panel block anchored to the clicked quadrant); GOC
   *  (derived-flank grammar) = 'column' (the clicked tile takes its full flank
   *  column, stack-mates collapse). Hero + ticker are never covered — the
   *  blow-up is bounded to this zone's own grid. */
  blowupMode?: 'quad' | 'column';
  /** Auto-restore window (ms) for a blown tile (Ruling 4.3 — ~5 min default). */
  blowupRestoreMs?: number;
  /** Whether to auto-fill empty tiles with curated YouTube-live news (#362) —
   *  TRUE only on the PEOC wall (`config.allowsNewsFallback`); the GOC wall has
   *  its own news TVs and must never rotate news into a flank. Default false so
   *  a caller that forgets to thread it never accidentally seats news. */
  allowsNews?: boolean;
}): JSX.Element {
  const slots = cols * rows;
  const { pins } = usePinnedFeeds();
  const escalation = useWallEscalation();
  // This bank's CANDIDATE pool (more than `slots` so the reel has feeds to rotate
  // through); the displayed window is sliced from it by the reel page below.
  const [candidates, setCandidates] = useState<WallFeed[]>([]);
  const [page, setPage] = useState(0);
  // Tile blow-up (#273, Ruling 4) — PRESENTATION state: per-window, ephemeral
  // (reload resets), mutates nothing server-side, audit-irrelevant (the ?dwell
  // class). `blown` = the feed id currently expanded; `blownCol` anchors the
  // PEOC 2×2 block to the clicked quadrant's column.
  const [blown, setBlown] = useState<{ id: string; col: number } | null>(null);

  // Pool depth — enough to fill a few reel pages without pulling the whole fleet.
  const poolSlots = slots * WALL_REEL_MAX_PAGES;
  // The bank's slice start in the shared ranking, scaled by the SAME reel depth
  // so sibling banks' POOLS are disjoint — not just their first pages. (With a
  // bare slot offset, left pool ranks 0…N overlap right pool ranks offset…M:
  // each bank's reel pages — and the dead-feed re-partition — would then
  // surface the SAME camera on both flanks; wall-v2 QA caught exactly that.)
  const poolOffset = feedOffset * WALL_REEL_MAX_PAGES;
  // An active escalation PAUSES the reel on its page (the escalated feed is first).
  const paused = !!escalation;

  useEffect(() => {
    let cancelled = false;
    const ctl = new AbortController();

    const refresh = async (): Promise<void> => {
      try {
        const [camsPriority, camsFeatured, hotspots, smoke, ice, flood] = await Promise.all([
          fetchCameras(CANADA_BBOX, ctl.signal, { smokePriority: true }),
          // Operator-curated wall pool (#362): featured cams join the candidate
          // set even when they aren't smoke-priority — a curated scenic vista is
          // wall-visible by virtue of being featured, not by the coverage flag
          // (which is ingest-owned and rarely true for scenic feeds).
          fetchCameras(CANADA_BBOX, ctl.signal, { featured: true }),
          fetchHighRiskHotspots(CANADA_BBOX, ctl.signal, { minConfidence: 60 }),
          fetchSmokeDetectionAnomalies(CANADA_BBOX, ctl.signal),
          fetchIceStormAnomalies(CANADA_BBOX, ctl.signal),
          fetchFloodAnomalies(CANADA_BBOX, ctl.signal),
        ]);
        if (cancelled) return;

        // Union the smoke-priority pool with the featured pool, deduped by id (a
        // featured cam that is ALSO smoke-priority appears in both fetches).
        const camById = new Map<string, CameraFeature>();
        for (const f of camsPriority.features) camById.set(f.id, f);
        for (const f of camsFeatured.features) camById.set(f.id, f);
        const camFeatures = [...camById.values()];

        const nowMs = Date.now();
        const cameras: WallCameraCandidate[] = camFeatures.map((f) => {
          const broadcast =
            classifyWallMedia(f.properties as unknown as WallMediaProps) === 'live';
          const featured = f.properties.wall_featured === true;
          // Stills exception (Ruling 5.5): a featured, non-broadcasting snapshot
          // is FRESH while captured ≤30 min ago. Since #382 freshness is a
          // seating PREFERENCE, not a gate — a fresh featured still is preferred
          // for its curated seat; a stale one still seats (below the fresh
          // ones), it no longer vacates. Freshness keys on the upstream capture
          // time, falling back to our last-ingested-frame stamp.
          const snapIso = f.properties.captured_at ?? f.properties.last_snapshot_at ?? null;
          const snapMs = snapIso ? Date.parse(snapIso) : NaN;
          const freshSnapshot =
            featured &&
            !broadcast &&
            Number.isFinite(snapMs) &&
            nowMs - snapMs <= FEATURED_SNAPSHOT_FRESH_MS;
          return {
            id: f.id,
            name: f.properties.name,
            lon: f.geometry.coordinates[0],
            lat: f.geometry.coordinates[1],
            live: f.properties.status === 'live',
            // Ruling 4 seating axis: a real mounted stream (#256 classification)
            // — snapshot/clip cameras are catalog-live but not broadcasting.
            broadcast,
            featured,
            freshSnapshot,
          };
        });
        const anchors: WallAlertAnchor[] = [
          ...hotspots.features.map((f) => toAnchor(hotspotToQueueItem(f))),
          ...smoke.features.map((f) => toAnchor(smokeToQueueItem(f))),
          ...ice.features.map((f) => toAnchor(iceStormToQueueItem(f))),
          ...flood.features.map((f) => toAnchor(floodToQueueItem(f))),
        ];

        // Select enough feeds to cover this bank's offset + reel depth, then keep
        // only its slice — so multiple banks (the flanks) share one ranking but
        // show distinct cameras. The left bank uses feedOffset 0 (no slice).
        // An escalation (if any) is surfaced FIRST and lands in the offset-0 bank.
        // `deadIds` feeds the Ruling 4 demotion: feeds whose mounted players
        // reported dead are re-seated only as a last resort.
        const selected = selectWallFeeds({
          alerts: anchors,
          cameras,
          pins: pins.map((p) => ({ kind: p.kind, id: p.id, label: p.label })),
          slots: poolOffset + poolSlots,
          escalated: toEscalatedTarget(escalation, Date.now()) ?? undefined,
          deadIds: wallDeadFeedIds(),
          // Each flank bank slices a `poolSlots`-wide region of this shared
          // ranking (sibling banks are symmetric → poolOffset is a multiple of
          // poolSlots), so a per-bank stills cap keyed on poolSlots admits ≤1
          // featured fresh snapshot per flank bank (#284, Ruling 5.5).
          bankSlots: poolSlots,
          // Curated YouTube-live news fills any slots the live-camera pool can't
          // (#334/#362) — a blank shows live news instead of "Awaiting feed…".
          // GATED to the PEOC wall (`allowsNews`): the GOC room has dedicated
          // news TVs, so `newsFallbackFor` returns an empty set and GOC flanks
          // never rotate news (the gate is unit-tested in wallNewsFallback.test).
          newsFallback: newsFallbackFor(allowsNews),
        });
        // Window this bank's slice of the shared ranking; backfill (deduped) when
        // the disjoint slice can't fill the visible page, so a flank never shows
        // "Awaiting feed…" while live feeds exist on the other flank (#371).
        setCandidates(bankWindow(selected, poolOffset, poolSlots, slots));
      } catch (err) {
        if ((err as { name?: string }).name !== 'AbortError') {
          console.error('WallMediaZone refresh failed', err);
        }
      }
    };

    void refresh();
    const timer = setInterval(() => void refresh(), MEDIA_REFRESH_MS);
    return () => {
      cancelled = true;
      ctl.abort();
      clearInterval(timer);
    };
    // pins/slots/offset/escalation/allowsNews drive the candidate set; fetches
    // are national.
  }, [pins, slots, poolOffset, poolSlots, escalation, allowsNews]);

  // Reel: rotate the visible page on a calm dwell. Pauses on an escalation and when
  // every candidate already fits on screen (single page). Reset to the first page
  // whenever the reel pauses so an escalated feed (always page 0) is on screen.
  // A BLOWN tile pins the reel too (Ruling 4.3 — the reel does not rotate a blown
  // tile mid-blow-up), but WITHOUT resetting the page, so the blown feed stays put.
  useEffect(() => {
    if (paused) {
      setPage(0);
      return undefined;
    }
    if (blown != null) return undefined; // pinned while a tile is blown
    if (!shouldAdvanceReel(candidates.length, slots, paused)) return undefined;
    const timer = setInterval(() => {
      setPage((p) => p + 1);
    }, dwellMs);
    return () => clearInterval(timer);
  }, [candidates.length, slots, paused, blown, dwellMs]);

  // Blow-up lifecycle (Ruling 4.3): auto-restore after ~5 min unless the
  // operator restores sooner; and a blown tile that DIES restores IMMEDIATELY
  // (a 2×2 / full-column OFFLINE placard is the worst dead wall — it then takes
  // the demotion path on the next seating pass).
  useEffect(() => {
    if (!blown) return undefined;
    const restore = setTimeout(() => setBlown(null), blowupRestoreMs);
    const death = setInterval(() => {
      if (wallDeadFeedIds().has(blown.id)) setBlown(null);
    }, BLOWUP_DEATH_POLL_MS);
    return () => {
      clearTimeout(restore);
      clearInterval(death);
    };
  }, [blown, blowupRestoreMs]);

  // Ruling 4's mid-dwell death allowance: a seated feed that DIED since the
  // last selection pass is moved behind every still-alive candidate at each
  // page tick, so the next reel rotation replaces it (≤1 transient non-live
  // tile while live candidates exist). Stable partition — order within each
  // class is the curated ranking. `page` re-evaluates this every rotation.
  const visible = useMemo(() => {
    const dead = wallDeadFeedIds();
    const alive = candidates.filter((f) => !dead.has(f.id));
    const died = candidates.filter((f) => dead.has(f.id));
    return reelWindow([...alive, ...died], page, slots);
  }, [candidates, page, slots]);

  // A seated tour card (PEOC, Ruling 2.5) takes the first tile; the feed
  // window narrows by one for the dwell. The CALLER decides card presence
  // (newness check upstream in WallZoneBody) — a quiet stop passes null and
  // keeps the full feed window, so no empty seat is ever reserved.
  const seatedCard = tourCard ?? null;
  const cardCells = seatedCard ? 1 : 0;

  // Tile blow-up layout (#273, Ruling 4.2). The blown feed must still be in the
  // current window (it rotated out / died → the blow-up has ended). A 2×2 block
  // needs ≥2 columns, so a 1-wide flank (GOC, or a 1-panel PEOC flank) blows up
  // as a full column instead. The hero + ticker are never touched — the blow-up
  // is bounded to THIS zone's grid.
  const blownFeed = blown ? (visible.find((f) => f.id === blown.id) ?? null) : null;
  const effectiveMode: 'quad' | 'column' =
    blowupMode === 'quad' && cols >= 2 ? 'quad' : 'column';
  const blownCells = blownFeed
    ? effectiveMode === 'quad'
      ? Math.min(4, slots - cardCells)
      : slots - cardCells // full flank column = the whole bank minus any card
    : 0;
  const otherCap = Math.max(0, slots - cardCells - blownCells);
  const feedWindow = (blownFeed ? visible.filter((f) => f.id !== blown!.id) : visible).slice(
    0,
    blownFeed ? otherCap : Math.max(0, slots - cardCells),
  );
  const blownAnchorCol =
    effectiveMode === 'quad' ? Math.min(Math.max(0, blown?.col ?? 0), cols - 2) : 0;
  const blownStyle: CSSProperties =
    effectiveMode === 'quad'
      ? { gridColumn: `${blownAnchorCol + 1} / span 2`, gridRow: '1 / span 2' }
      : { gridColumn: `1 / span ${cols}`, gridRow: `1 / span ${rows}` };

  // The bank's tile grid (PART X): cols × rows full-bleed tiles. `dense` auto-
  // flow lets the displaced tiles reflow cleanly around a blown 2×2 block.
  return (
    <section
      data-testid="wall-media-zone"
      data-paused={paused ? 'true' : 'false'}
      data-blown={blownFeed ? 'true' : 'false'}
      className="grid h-full w-full bg-black"
      style={{
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
        gridAutoFlow: 'dense',
      }}
    >
      {blownFeed && (
        <WallFeedSlot
          key={`blown-${blownFeed.id}`}
          feed={blownFeed}
          blown
          style={blownStyle}
          onClick={() => setBlown(null)}
        />
      )}
      {seatedCard}
      {feedWindow.length === 0 && !blownFeed
        ? Array.from({ length: Math.max(0, slots - cardCells) }).map((_, i) => (
            <WallFeedPlaceholder key={`ph-${i}`} />
          ))
        : feedWindow.map((f, i) => (
            <WallFeedSlot
              key={f.id}
              feed={f}
              onClick={() => setBlown({ id: f.id, col: (cardCells + i) % cols })}
            />
          ))}
    </section>
  );
}

/** A clickable wall tile (#273, Ruling 4): click toggles the tile's blow-up.
 *  The blow-up is PRESENTATION state — a div role=button (not a real <button>,
 *  which can't legally wrap interactive media) keeps the wall read-only for
 *  OPERATIONAL state while allowing the local zoom. */
function WallFeedSlot({
  feed,
  onClick,
  blown = false,
  style,
}: {
  feed: WallFeed;
  onClick: () => void;
  blown?: boolean;
  style?: CSSProperties;
}): JSX.Element {
  return (
    <div
      data-testid="wall-feed-slot"
      data-blown={blown ? 'true' : 'false'}
      role="button"
      tabIndex={0}
      aria-label={blown ? `Restore ${feed.label}` : `Expand ${feed.label}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className={`relative min-h-0 min-w-0 cursor-pointer overflow-hidden focus:outline-none ${
        blown ? 'z-10 ring-2 ring-indigo-400/70' : ''
      }`}
      style={style}
    >
      <WallFeedTile feed={feed} />
    </div>
  );
}

function WallFeedPlaceholder(): JSX.Element {
  return (
    <div
      data-testid="wall-feed-placeholder"
      className="flex items-center justify-center border-r border-slate-900 bg-slate-950 text-xs text-slate-600"
    >
      Awaiting feed…
    </div>
  );
}

/** One borderless wall tile: full-bleed media + an overlay caption with a LIVE
 *  dot. The frame letterboxes (object-contain on black) so it is never cropped.
 *  A news feed (#182) or drone feed (#216) is an ambient pinned feed — rendered
 *  via the shared YouTube player (no camera fetch, no anomaly treatment). */
function WallFeedTile({ feed }: { feed: WallFeed }): JSX.Element {
  if (feed.kind === 'news') return <WallNewsTile feed={feed} />;
  if (feed.kind === 'drone') return <WallDroneTile feed={feed} />;
  return <WallCameraTile feed={feed} />;
}

/** Caption-dot treatment per honest label (#232): ONLY confirmed-playing live
 *  video pulses (wall motion law); still frames sit in the ambient snapshot-
 *  blue family; offline / connecting / no-picture are muted slate. */
function captionDotClass(label: WallMediaLabelInfo | null): string {
  if (!label) return 'bg-slate-500';
  if (label.pulse) return 'animate-pulse bg-emerald-400';
  if (label.kind === 'clip' || label.kind === 'snapshot') return 'bg-sky-400';
  return 'bg-slate-500';
}

function WallCameraTile({ feed }: { feed: WallFeed }): JSX.Element {
  const [camera, setCamera] = useState<CameraFeature | null>(null);
  const [failed, setFailed] = useState(false);
  // Wall-clock of the tile's last genuinely-fresh proxied snapshot (QA-3 (c)) —
  // lets the caption show the honest live age over a stale cached DB stamp.
  const [proxiedAtMs, setProxiedAtMs] = useState<number | null>(null);
  // The mounted player's ACTUAL runtime — a catalog-live camera whose stream
  // isn't broadcasting must caption OFFLINE, exactly like its tile body. The
  // state also feeds the curation registry (#269): a dead report demotes this
  // feed at the next seating pass so the rotation replaces it.
  const [playback, setPlayback] = useState<WallPlaybackState>('loading');
  const onPlayback = useCallback(
    (state: WallPlaybackState): void => {
      setPlayback(state);
      reportWallFeedPlayback(feed.id, state);
    },
    [feed.id],
  );

  useEffect(() => {
    let cancelled = false;
    setCamera(null);
    setFailed(false);
    setProxiedAtMs(null);
    setPlayback('loading');
    void fetch(`/api/cameras/${feed.id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((feat: CameraFeature | null) => {
        if (cancelled) return;
        if (feat && (feat as { type?: string }).type === 'Feature') setCamera(feat);
        else setFailed(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [feed.id]);

  // Honest caption state (#232 finding 4): derived from the SAME #256 axes the
  // media body routes playback on — never from the catalog status alone, so
  // "LIVE" can never overlay a frame the tile is honestly showing as old.
  const label = camera ? wallMediaLabelForCamera(camera, Date.now(), proxiedAtMs, playback) : null;
  // Why this feed is on the wall — omitted for plain cycle feeds (no context
  // beats a filler word beside the honest state).
  const context =
    feed.reason === 'escalated'
      ? `escalated${feed.nearAlert ? ` · ${feed.nearAlert}` : ''}`
      : feed.reason === 'alert' && feed.nearAlert
        ? `near ${feed.nearAlert}`
        : feed.reason === 'pinned'
          ? 'pinned'
          : null;

  return (
    <div data-testid="wall-feed-tile" className="relative h-full w-full overflow-hidden bg-black">
      {/* Full-bleed media: CameraMedia's wall variant fills + centre-crops and
          drops the panel chrome explicitly (no CSS reach-ins) — uniform mosaic
          tiles regardless of source aspect (#232 finding 2). */}
      <div className="absolute inset-0 flex items-center justify-center">
        {failed ? (
          <span className="text-xs text-slate-600">Feed unavailable</span>
        ) : camera ? (
          <CameraMedia
            camera={camera}
            enablePopout={false}
            variant="wall"
            onFreshFrame={() => setProxiedAtMs(Date.now())}
            onPlaybackState={onPlayback}
          />
        ) : (
          <span className="text-xs text-slate-600">Loading…</span>
        )}
      </div>

      {/* Overlay caption — identifies the feed at wall distance, and carries the
          honest media state + freshness ("SNAPSHOT · captured 19 hours ago"). */}
      <div
        data-testid="wall-feed-caption"
        className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-black/85 to-transparent px-3 pb-2 pt-6"
      >
        <span
          className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${captionDotClass(label)}`}
          title={label?.state ?? 'loading'}
        />
        <span className="truncate text-base font-semibold text-slate-50 drop-shadow">
          {feed.label}
        </span>
        <span
          data-testid="wall-feed-state"
          className="shrink-0 text-xs font-medium uppercase tracking-wider text-slate-300"
        >
          {label
            ? [label.state, label.freshness, context].filter(Boolean).join(' · ')
            : '…'}
        </span>
      </div>
    </div>
  );
}

/** A pinned news outlet (#182) on the wall mosaic: the shared YouTube player
 *  resolves its current live, full-bleed + borderless like a camera tile, with a
 *  "NEWS" caption suffix. Ambient — no red/anomaly treatment, just a live tile.
 *  fill+chromeless (#232): the embed is centre-cropped to the tile with player
 *  chrome suppressed as far as the iframe permits (see YouTubeLivePlayer). */
function WallNewsTile({ feed }: { feed: WallFeed }): JSX.Element {
  return (
    <div data-testid="wall-feed-tile" data-feed-kind="news" className="relative h-full w-full overflow-hidden bg-black">
      <div className="absolute inset-0 [&_button]:hidden">
        <NewsMedia id={feed.id} label={feed.label} fill chromeless />
      </div>
      <div
        data-testid="wall-feed-caption"
        className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-black/85 to-transparent px-3 pb-2 pt-6"
      >
        <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-sky-400" title="news" />
        <span className="truncate text-base font-semibold text-slate-50 drop-shadow">{feed.label}</span>
        <span className="shrink-0 text-xs font-medium uppercase tracking-wider text-slate-300">
          {/* Honest context (#232): a pinned outlet says "pinned"; an auto-seated
              news-fallback tile (#362, reason 'cycle') just reads "NEWS" — it was
              never operator-pinned. The player body carries the live/not-live dot. */}
          {feed.reason === 'pinned' ? 'NEWS · pinned' : 'NEWS'}
        </span>
      </div>
    </div>
  );
}

/** A pinned drone feed (#216) on the wall mosaic: same shared YouTube player as a
 *  news tile, full-bleed + borderless, with a "DRONE" caption suffix. Ambient — no
 *  red/anomaly treatment, just a live aerial tile. fill+chromeless as news. */
function WallDroneTile({ feed }: { feed: WallFeed }): JSX.Element {
  return (
    <div data-testid="wall-feed-tile" data-feed-kind="drone" className="relative h-full w-full overflow-hidden bg-black">
      <div className="absolute inset-0 [&_button]:hidden">
        <DroneMedia id={feed.id} label={feed.label} fill chromeless />
      </div>
      <div
        data-testid="wall-feed-caption"
        className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center gap-2 bg-gradient-to-t from-black/85 to-transparent px-3 pb-2 pt-6"
      >
        <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-violet-400" title="drone" />
        <span className="truncate text-base font-semibold text-slate-50 drop-shadow">{feed.label}</span>
        <span className="shrink-0 text-xs font-medium uppercase tracking-wider text-slate-300">
          DRONE · pinned
        </span>
      </div>
    </div>
  );
}
